import { Clock, Loader2, Share2, Timer, X, Zap } from 'lucide-react';
import React, { useState } from 'react';

import { Panel } from '@/components/ui/panel';
import { type Coin, useTokenPairStore } from '@/features/tokenPairs';
import { cn } from '@/lib/utils';
import type { ConnectionState } from '@/lib/ws';

import { useCloseActiveTrade } from '../hooks/useCloseActiveTrade';
import { useExecutionDisplay } from '../hooks/useExecutionDisplay';
import { useLimitOrderActions } from '../hooks/useLimitOrderActions';
import type { PriceConfig } from '../hooks/usePriceConfig';
import { type TerminalRow, useTerminalRows } from '../hooks/useTerminalRows';
import { useTimeFormat } from '../hooks/useTimeFormat';
import { formatRowTime, formatTimestampTitle } from '../utils/tradeTime';

import ChainLoadIndicator from './ChainLoadIndicator';
import PnlShareDialog, { type PnlShareRequest } from './PnlShareDialog';
import {
    ChartedPnlCell,
    OffChartPnlCell,
    RestingDistanceCell,
} from './PositionPnlCells';

/**
 * POSITIONS / HISTORY table in the `terminal` grid area. Reads the
 * active/closed trade queries in real mode and the local history slice in
 * demo, and offers the close action for unmanaged trades; the managed
 * position row closes through the trade rail's CLOSE flow.
 */

interface SpeedTerminalProps extends React.ComponentProps<'div'> {
    connectionState: ConnectionState;
    latency: number | null;
    isStale: boolean;
    priceConfig: PriceConfig;
    onClosePosition: () => void;
    /** Cancels the CHARTED market's resting order, through the rail's flow. */
    onCancelLimitOrder: () => void;
    /** Fills the CHARTED market's resting order, through the rail's flow. */
    onFillLimitOrder: () => void;
}

const SideChip: React.FC<{ side: 'LONG' | 'SHORT' }> = ({ side }) => (
    <span
        className={cn(
            'rounded-full px-2 py-0.5 text-[9px] font-bold uppercase',
            side === 'LONG'
                ? 'bg-primary/15 text-primary'
                : 'bg-destructive/15 text-destructive',
        )}
    >
        {side === 'LONG' ? 'Long' : 'Short'}
    </span>
);

// Memoized: SpeedTerminal no longer takes a per-tick currentValue prop,
// so it only re-renders when trades / tab / connection actually change.
const SpeedTerminalImpl: React.FC<SpeedTerminalProps> = ({
    connectionState,
    latency,
    isStale,
    priceConfig,
    onClosePosition,
    onCancelLimitOrder,
    onFillLimitOrder,
    className,
    style,
    ...divProps
}) => {
    const [tab, setTab] = useState<'positions' | 'limits' | 'history'>(
        'positions',
    );
    const [shareRequest, setShareRequest] = useState<PnlShareRequest | null>(
        null,
    );

    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    const setSelectedCoin = useTokenPairStore((s) => s.setSelectedCoin);
    const { closeTrade, closeStates } = useCloseActiveTrade();
    const { cancelOrder, fillOrder, orderStates } = useLimitOrderActions();
    // the charted row shares the rail's in-flight state, since it shares its
    // handlers — one order, one spinner, wherever it is pressed from.
    const { executionInFlight } = useExecutionDisplay();
    const {
        positionRows,
        historyRows,
        limitRows,
        historyErrored,
        resolveShare,
    } = useTerminalRows(priceConfig);
    // Wall clock vs "x ago", flipped from the column header and remembered.
    const { format: timeFormat, toggle: toggleTimeFormat } = useTimeFormat();

    const openShare = (row: TerminalRow) => {
        const request = resolveShare(row);
        if (request) setShareRequest(request);
    };

    const rows =
        tab === 'positions'
            ? positionRows
            : tab === 'limits'
                ? limitRows
                : historyRows;

    const connValue =
        connectionState === 'connected'
            ? latency !== null
                ? `${latency}ms`
                : 'LIVE'
            : connectionState === 'reconnecting'
                ? 'RETRY'
                : connectionState === 'connecting'
                    ? 'INIT'
                    : 'OFF';

    return (
        <Panel
            as="section"
            aria-label="Positions and trade history"
            className={cn('flex flex-col overflow-hidden', className)}
            style={{ ...style, height: '200px' }}
            {...divProps}
        >
            {/* Tabs */}
            <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2">
                {(
                    [
                        ['positions', 'Positions', positionRows.length],
                        ['limits', 'Limit orders', limitRows.length],
                        ['history', 'History', historyRows.length],
                    ] as const
                ).map(([key, label, count]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setTab(key)}
                        aria-pressed={tab === key}
                        className={cn(
                            'flex cursor-pointer items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors',
                            tab === key
                                ? 'text-foreground'
                                : 'text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {label}
                        <span
                            className={cn(
                                'rounded-full px-1.5 py-px font-mono text-[9px] font-bold',
                                tab === key
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground',
                            )}
                        >
                            {count}
                        </span>
                    </button>
                ))}
                {/* Telemetry cluster: HyperEVM chain load (self-hides when the
                    backend feature is off) alongside the feed's own connection
                    pill — both are ambient status, so they read as one group. */}
                <span className="ml-auto flex items-center gap-2">
                    <ChainLoadIndicator />
                    <span className="flex items-center gap-1.5 font-mono text-[10px] tabular-nums text-muted-foreground">
                        <span
                            className={cn(
                                'inline-block size-1.5 rounded-full',
                                connectionState === 'connected' && !isStale
                                    ? 'bg-primary'
                                    : 'bg-amber-400',
                            )}
                        />
                        {connValue}
                    </span>
                </span>
            </div>

            {/* Table */}
            <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="grid grid-cols-[1.2fr_1.7fr_0.7fr_0.8fr_1fr_1.6fr_auto] items-center gap-2 px-4 py-1.5 text-[10px] font-semibold text-muted-foreground">
                    <button
                        type="button"
                        onClick={toggleTimeFormat}
                        aria-pressed={timeFormat === 'relative'}
                        title={
                            timeFormat === 'relative'
                                ? 'Show the date and clock time'
                                : 'Show how long ago'
                        }
                        className="flex cursor-pointer items-center gap-1 justify-self-start font-semibold transition-colors hover:text-foreground"
                    >
                        Time
                        {timeFormat === 'relative' ? (
                            <Timer size={9} className="opacity-70" />
                        ) : (
                            <Clock size={9} className="opacity-70" />
                        )}
                    </button>
                    <span>Market / Side</span>
                    <span>Lev</span>
                    <span>Margin</span>
                    <span>{tab === 'limits' ? 'Limit / Entry' : 'Entry'}</span>
                    <span>{tab === 'limits' ? 'Distance / PnL' : 'PnL'}</span>
                    <span className="w-6" />
                </div>
                {rows.length === 0 ? (
                    <div className="flex items-center justify-center px-4 py-6 text-xs text-muted-foreground">
                        {tab === 'positions'
                            ? 'No open positions, pick a side to get started.'
                            : tab === 'limits'
                                ? historyErrored
                                    ? 'Limit order history is unavailable right now.'
                                    : 'No limit orders yet. Choose Limit below leverage to place one.'
                                : historyErrored
                                    ? // same rule as TradeTerminal: a failed history fetch must not
                                // masquerade as an empty history.
                                    'Trade history is unavailable right now, retrying.'
                                    : 'No trades yet.'}
                    </div>
                ) : (
                    rows.map((row) => {
                        const closing =
                            row.tradeHash !== undefined &&
                            closeStates.get(row.tradeHash) === 'closing';
                        const orderBusy = orderStates.get(row.key) === 'busy';
                        // Clicking a row retargets the chart and the rail to that
                        // market — the interaction the "rail follows the chart"
                        // model rests on (see MULTI_POSITION_PLAN.md). Useful
                        // today with one position; required once several can be
                        // open at once, because the rail only ever shows the
                        // selected market's.
                        const retarget = row.coin
                            ? () => setSelectedCoin(row.coin as Coin)
                            : null;
                        const isSelected =
                            !!row.coin &&
                            row.coin.toUpperCase() ===
                                selectedCoin.toUpperCase();
                        return (
                            <div
                                key={row.key}
                                {...(retarget && {
                                    role: 'button',
                                    tabIndex: 0,
                                    'aria-current': isSelected,
                                    'aria-label': `Show ${row.market} on the chart`,
                                    onClick: retarget,
                                    onKeyDown: (
                                        event: React.KeyboardEvent<HTMLDivElement>,
                                    ) => {
                                        if (
                                            event.key === 'Enter' ||
                                            event.key === ' '
                                        ) {
                                            event.preventDefault();
                                            retarget();
                                        }
                                    },
                                })}
                                className={cn(
                                    'grid grid-cols-[1.2fr_1.7fr_0.7fr_0.8fr_1fr_1.6fr_auto] items-center gap-2 border-t border-border/40 px-4 py-1.5 font-mono text-xs tabular-nums transition-colors',
                                    retarget &&
                                        'cursor-pointer hover:bg-secondary/50 focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-ring',
                                    !retarget && 'hover:bg-secondary/50',
                                    // the market already on the chart: a lime
                                    // spine, matching how the markets sidebar
                                    // marks the active row.
                                    isSelected &&
                                        'bg-primary/[0.06] shadow-[inset_2px_0_0_0_var(--primary)]',
                                )}
                            >
                                <span
                                    className="whitespace-nowrap text-muted-foreground"
                                    title={formatTimestampTitle(row.at)}
                                >
                                    {formatRowTime(row.at, timeFormat)}
                                </span>
                                <span className="flex items-center gap-2 font-sans font-semibold text-foreground">
                                    {row.market}
                                    <SideChip side={row.side} />
                                    {row.status && (
                                        <span
                                            className={cn(
                                                'font-mono text-[9px] font-semibold uppercase tracking-[0.08em]',
                                                row.status.className,
                                            )}
                                        >
                                            {row.status.label}
                                        </span>
                                    )}
                                </span>
                                <span className="text-foreground/70">
                                    {row.leverage}
                                </span>
                                <span className="text-foreground/80">
                                    {row.margin}
                                </span>
                                <span className="text-foreground/70">
                                    {row.entry}
                                </span>
                                {row.resting ? (
                                    <RestingDistanceCell
                                        coin={row.coin ?? ''}
                                        limitPrice={row.limitPrice ?? 0}
                                    />
                                ) : row.live === 'charted' ? (
                                    <ChartedPnlCell />
                                ) : row.live === 'offChart' && row.coin ? (
                                    <OffChartPnlCell coin={row.coin} />
                                ) : row.pnl !== null ? (
                                    <span
                                        className={cn(
                                            'font-bold',
                                            row.pnlPositive
                                                ? 'text-success'
                                                : 'text-destructive',
                                        )}
                                    >
                                        {row.pnl}
                                    </span>
                                ) : (
                                    <span className="text-muted-foreground">
                                        —
                                    </span>
                                )}
                                {/* Share and close must not bubble into the row's
                            retarget — pressing Close should close the
                            trade, not also swap the chart to it. */}
                                <span
                                    className="flex w-12 justify-end gap-0.5"
                                    onClick={(event) => event.stopPropagation()}
                                    onKeyDown={(event) =>
                                        event.stopPropagation()
                                    }
                                >
                                    {(row.share || row.shareDemo) && (
                                        <button
                                            type="button"
                                            title="Share this PnL"
                                            onClick={() => openShare(row)}
                                            className="cursor-pointer p-0.5 text-muted-foreground transition-colors hover:text-primary"
                                        >
                                            <Share2 size={11} />
                                        </button>
                                    )}
                                    {/* ONLY the charted row closes through the rail —
                                `onClosePosition` targets the charted market, so
                                letting an off-chart row use it would close the
                                wrong position. Those go through closeTrade,
                                which takes the trade's own hash and coin. */}
                                    {/* A resting order is not closed, it is
                                        cancelled — or taken at market. The
                                        CHARTED one goes through the rail's
                                        handlers so the execution slot driving
                                        the rail is the thing that changes;
                                        acting on it from here would leave that
                                        slot stuck on an order that is gone. */}
                                    {row.resting && isSelected ? (
                                        <>
                                            <button
                                                type="button"
                                                title="Fill now"
                                                disabled={executionInFlight}
                                                onClick={onFillLimitOrder}
                                                className={cn(
                                                    'cursor-pointer p-0.5',
                                                    executionInFlight
                                                        ? 'cursor-wait text-muted-foreground'
                                                        : 'text-muted-foreground hover:text-foreground',
                                                )}
                                            >
                                                {executionInFlight ? (
                                                    <Loader2
                                                        size={11}
                                                        className="animate-spin"
                                                    />
                                                ) : (
                                                    <Zap size={11} />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                title="Cancel order"
                                                disabled={executionInFlight}
                                                onClick={onCancelLimitOrder}
                                                className={cn(
                                                    'cursor-pointer p-0.5',
                                                    executionInFlight
                                                        ? 'cursor-wait text-muted-foreground'
                                                        : 'text-destructive hover:bg-destructive/10',
                                                )}
                                            >
                                                <X size={11} />
                                            </button>
                                        </>
                                    ) : row.resting && row.coin ? (
                                        <>
                                            <button
                                                type="button"
                                                title="Fill now"
                                                disabled={orderBusy}
                                                onClick={() => {
                                                    void fillOrder(
                                                        row.key,
                                                        row.coin!,
                                                        row.tradeHash,
                                                    );
                                                }}
                                                className={cn(
                                                    'cursor-pointer p-0.5',
                                                    orderBusy
                                                        ? 'cursor-wait text-muted-foreground'
                                                        : 'text-muted-foreground hover:text-foreground',
                                                )}
                                            >
                                                {orderBusy ? (
                                                    <Loader2
                                                        size={11}
                                                        className="animate-spin"
                                                    />
                                                ) : (
                                                    <Zap size={11} />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                title="Cancel order"
                                                disabled={orderBusy}
                                                onClick={() => {
                                                    void cancelOrder(
                                                        row.key,
                                                        row.coin!,
                                                        row.tradeHash,
                                                    );
                                                }}
                                                className={cn(
                                                    'cursor-pointer p-0.5',
                                                    orderBusy
                                                        ? 'cursor-wait text-muted-foreground'
                                                        : 'text-destructive hover:bg-destructive/10',
                                                )}
                                            >
                                                <X size={11} />
                                            </button>
                                        </>
                                    ) : row.live === 'charted' ? (
                                        <button
                                            type="button"
                                            title="Close position"
                                            onClick={onClosePosition}
                                            className="cursor-pointer p-0.5 text-destructive hover:bg-destructive/10"
                                        >
                                            <X size={11} />
                                        </button>
                                    ) : row.tradeHash && row.coin ? (
                                        <button
                                            type="button"
                                            title="Close trade"
                                            disabled={!!closing}
                                            onClick={() => {
                                                void closeTrade(
                                                    row.tradeHash!,
                                                    row.coin!,
                                                );
                                            }}
                                            className={cn(
                                                'cursor-pointer p-0.5',
                                                closing
                                                    ? 'cursor-wait text-muted-foreground'
                                                    : 'text-destructive hover:bg-destructive/10',
                                            )}
                                        >
                                            {closing ? (
                                                <Loader2
                                                    size={11}
                                                    className="animate-spin"
                                                />
                                            ) : (
                                                <X size={11} />
                                            )}
                                        </button>
                                    ) : null}
                                </span>
                            </div>
                        );
                    })
                )}
            </div>

            {/* On-demand PnL card (opened from a position or history row) */}
            {shareRequest && (
                <PnlShareDialog
                    request={shareRequest}
                    onDismiss={() => setShareRequest(null)}
                />
            )}
        </Panel>
    );
};

const SpeedTerminal = React.memo(SpeedTerminalImpl);

export default SpeedTerminal;
